import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Bookmark, BookmarkCheck, Check } from 'lucide-react';
import PageShell from '../components/PageShell';
import PillFilter from '../components/PillFilter';
import SegmentedToggle from '../components/SegmentedToggle';
import SAVOIRS from '../data/savoirs';
import { readingMinutes, setFichePicked, useFicheProgress } from '../data/ficheProgress';
import { BackHome } from './WelcomeNewsPage';
import './WelcomePage.css';

/* Fiches de formation (2026-10-09) : la présentation des Guides, une carte
   par fiche, deux de front, filtrées par catégorie. Chaque carte dit sa durée
   de lecture estimée et si elle est lue ; « Lire la fiche » l'ouvre dans sa
   fenêtre, la même que depuis le panneau Savoirs. La personne RETIENT les
   fiches utiles pour elle (« Ma sélection »), et la barre dit où elle en est
   de cette sélection. */
export default function WelcomeFichesPage({ setPage, onOpenFiche }) {
  const { t } = useTranslation();
  const h = (k, o) => t(`cockpit:home.${k}`, o);
  const { read, picked } = useFicheProgress();
  const cats = useMemo(() => [...new Set(SAVOIRS.map((f) => f.category).filter(Boolean))], []);
  const mins = useMemo(() => Object.fromEntries(SAVOIRS.map((f) => [f.id, readingMinutes(f)])), []);
  const [cat, setCat] = useState('all');
  const [scope, setScope] = useState('all');
  const [state, setState] = useState('all');

  const mine = SAVOIRS.filter((f) => picked[f.id]);
  const mineRead = mine.filter((f) => read[f.id]).length;
  const pct = mine.length ? Math.round((100 * mineRead) / mine.length) : 0;

  const cards = SAVOIRS
    .filter((f) => cat === 'all' || f.category === cat)
    .filter((f) => scope === 'all' || picked[f.id])
    .filter((f) => state === 'all' || (state === 'read') === !!read[f.id]);

  return (
    <PageShell title={t('nav:welcomeFiches')} actions={<BackHome setPage={setPage} />}>
      <div className="wch">
        {SAVOIRS.length === 0 && <p className="wch-empty">{h('noFiches')}</p>}
        {/* VARIANTE A du proto (2026-10-09, `WIP/protos/fiches-filtres.html`) :
            ma sélection et ma progression sur leur ligne ; sous un filet, les
            filtres, la lecture d'abord, le type ensuite, qui se lit comme une
            étiquette. Chaque libellé AU-DESSUS de sa commande. */}
        {SAVOIRS.length > 0 && (
          <>
            <div className="wch-fsel">
              <div className="wch-fgroup">
                <span className="lk-filter-lbl">{h('show')}</span>
                <SegmentedToggle value={scope} onChange={setScope}
                                 options={[{ value: 'all', label: <>{h('allFiches')} <span className="wch-cnt">{SAVOIRS.length}</span></> },
                                   { value: 'mine', label: <>{h('myPick')} <span className="wch-cnt">{mine.length}</span></> }]} />
              </div>
              {mine.length > 0 && (
                <div className="wch-fgroup wch-fgroup--grow">
                  <span className="lk-filter-lbl">{h('myProgress')}</span>
                  <div className="wch-progress">
                    <div className="wch-progress__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100}
                         aria-valuenow={pct} aria-label={h('myProgress')}>
                      <span style={{ width: `${pct}%` }} />
                    </div>
                    <span className="wch-progress__v">
                      <b>{mineRead} / {mine.length}</b> {h('readShort')} · {pct} %
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="wch-filter">
              <div className="wch-fgroup">
                <span className="lk-filter-lbl">{h('reading')}</span>
                <SegmentedToggle value={state} onChange={setState}
                                 options={[{ value: 'all', label: h('allF') },
                                   { value: 'todo', label: h('toRead') },
                                   { value: 'read', label: h('readMany') }]} />
              </div>
              {cats.length > 1 && (
                <div className="wch-fgroup">
                  <span className="lk-filter-lbl">{h('type')}</span>
                  <PillFilter multiple={false} value={cat} onChange={setCat}
                              options={[{ value: 'all', label: h('allF') },
                                ...cats.map((c) => ({ value: c, label: c }))]} />
                </div>
              )}
            </div>
          </>
        )}
        <div className="wch-guides">
          {cards.map((f) => {
            const isPicked = !!picked[f.id];
            return (
              <div key={f.id} className={`wch-guide wch-fiche${isPicked ? ' is-picked' : ''}`}>
                {/* Le titre de la fiche EN TÊTE, comme un titre de carte, sa
                    catégorie et sa durée dessous (2026-10-09). */}
                <div className="wch-fiche__head">
                  <h3>{f.title}</h3>
                  {read[f.id] && (
                    <span className="wch-guide__done">
                      {h('ficheRead')}
                      <span className="wch-check" aria-hidden="true"><Check size={10} strokeWidth={3} /></span>
                    </span>
                  )}
                </div>
                <span className="wch-guide__meta">{f.category} · {h('minutes', { n: mins[f.id] })}</span>
                {f.summary && <p>{f.summary}</p>}
                <div className="wch-fiche__foot">
                  <button type="button" className="wch-fiche__open" tabIndex={0} onClick={() => onOpenFiche && onOpenFiche(f.id)}>
                    {h('readFiche')} <ArrowRight size={13} strokeWidth={1.8} aria-hidden="true" />
                  </button>
                  <button type="button" className="wch-fiche__pick" aria-pressed={isPicked}
                          onClick={() => setFichePicked(f.id, !isPicked)}>
                    {isPicked ? h('pickIn') : h('pickAdd')}
                    {isPicked
                      ? <BookmarkCheck size={14} strokeWidth={1.9} aria-hidden="true" />
                      : <Bookmark size={14} strokeWidth={1.9} aria-hidden="true" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {SAVOIRS.length > 0 && !cards.length && <p className="wch-empty">{h('noFichesHere')}</p>}
      </div>
    </PageShell>
  );
}
